/**
 * THE COMPLIANCE QUEUE, read from the database.
 *
 * Three calls, and the split between them is the specification rather than a
 * convenience:
 *
 *   queue   — the work list. Carries no email, phone, owner name or document,
 *             so refreshing your own workload is not an act of personal-data
 *             access and needs no reason. Canon 4.2 asks for a reason before a
 *             RECORD renders, not before a list of work does.
 *
 *   open    — one application in full. The reason is a REQUIRED ARGUMENT of
 *             the database function, and the log entry is written before the
 *             row is read. This module could not skip it if it tried.
 *
 *   decide  — approve or reject. Idempotent in the database, so a
 *             double-clicked button is not a second provider.
 */

import { supabase, isSupabaseConfigured } from './supabaseClient';

const fail = (error) => ({ data: null, error });

/** @param {'submitted'|'under_review'|'approved'|'rejected'|'activated'|null} status */
export async function fetchApplicationQueue(status = 'submitted') {
  if (!isSupabaseConfigured()) return fail('This portal cannot reach the database.');
  const { data, error } = await supabase.rpc('fn_provider_application_queue', {
    p_status: status,
  });
  if (error) return fail(error.message || 'The queue could not be loaded.');
  return { data: data || [], error: null };
}

/**
 * Open one application. There is no version of this without a reason, because
 * there is no version of the database function without one.
 */
export async function openApplication(id, reason) {
  if (!isSupabaseConfigured()) return fail('This portal cannot reach the database.');
  const { data, error } = await supabase.rpc('fn_provider_application', {
    p_id: id,
    p_reason: reason,
  });
  if (error) return fail(error.message || 'That application could not be opened.');
  // RETURNS TABLE with one row. PostgREST hands back an array.
  return { data: Array.isArray(data) ? data[0] || null : data, error: null };
}

export async function decideApplication({ id, decision, reason, notes = null }) {
  if (!isSupabaseConfigured()) return fail('This portal cannot reach the database.');
  const { data, error } = await supabase.rpc('fn_decide_provider_application', {
    p_id: id,
    p_decision: decision,
    p_reason: reason,
    p_notes: notes,
  });
  if (error) return fail(error.message || 'That decision could not be recorded.');
  return { data: Array.isArray(data) ? data[0] || null : data, error: null };
}

/**
 * How long somebody has been waiting, said the way a person would.
 *
 * The queue is sorted oldest-first for a reason, and a column of raw integers
 * makes that ordering hard to feel. "11 days" reads as a delay; "11" reads as
 * data.
 */
export function waitingLabel(days) {
  const n = Number(days) || 0;
  if (n <= 0) return 'Today';
  if (n === 1) return '1 day';
  return `${n} days`;
}

/**
 * Whether a wait has become a problem. Not a service-level promise -- there is
 * no published one for applications yet -- but a reviewer looking at a list
 * should be able to see where the oldest work is without reading every number.
 */
export function waitTone(days) {
  const n = Number(days) || 0;
  if (n >= 14) return 'critical';
  if (n >= 7) return 'warning';
  return 'quiet';
}

// ---------------------------------------------------------------------------
// PROVIDER VERIFICATION DOCUMENTS
//
//   "how does a provider get verified? if it's something admin has to do, write
//    the path for it in the Orchid Portal."
//
// It is something admin has to do, and this is that path. The Verified badge is
// derived from four facts — owner identity, a verified bank account, CIPC
// registration for a juristic entity, current public liability cover where the
// trade requires it — and until script 73 there was no route from a document a
// provider had sent to any of them.
//
// The split mirrors the application queue above and for the same reason: the
// queue is a work list carrying no personal data, and the decision is the act
// that needs the permission and leaves the record.

/** The work list. Oldest first — the bottom of a newest-first queue never gets read. */
export async function fetchVerificationQueue() {
  if (!isSupabaseConfigured()) return fail('This portal cannot reach the database.');
  const { data, error } = await supabase.rpc('fn_verification_queue');
  if (error) {
    if (error.code === 'PGRST202') {
      return fail('The verification queue is not on this database yet — script 73 has not been run.');
    }
    if (error.code === '42501') {
      return fail('Your account does not hold Approve Verification.');
    }
    return fail(error.message || 'The queue could not be loaded.');
  }
  return { data: data || [], error: null };
}

/**
 * Accept or reject one document.
 *
 * Accepting is what writes the verified timestamp the badge is computed from —
 * there is no other route to it, by design. A rejection must carry a reason,
 * and the database refuses one shorter than a sentence, because the reason is
 * what the provider reads on their own screen and acts on.
 */
export async function reviewProviderDocument({ submissionId, accept, note }) {
  if (!isSupabaseConfigured()) return fail('This portal cannot reach the database.');
  if (!submissionId) return fail('Choose a document.');
  if (!accept && (note || '').trim().length < 10) {
    return fail('Say why, in a sentence they can act on. "No" tells them nothing to fix.');
  }

  const { data, error } = await supabase.rpc('fn_review_provider_document', {
    p_submission_id: submissionId,
    p_accept: Boolean(accept),
    p_note: (note || '').trim() || null,
  });
  if (error) return fail(error.message || 'That decision could not be recorded.');
  const row = Array.isArray(data) ? data[0] : data;
  return { data: row, error: null };
}

/**
 * The people waiting on Orchid to check their identity documents.
 *
 * A DIFFERENT QUEUE FROM fetchVerificationQueue, and the distinction is the
 * whole reason this exists. That one lists PROVIDER DOCUMENT SUBMISSIONS — its
 * rows are businesses and the papers a business files. This one lists
 * INDIVIDUALS whose fica_verifications record is still pending: a landlord or
 * a tenant who has handed in an ID and a proof of address and is waiting.
 *
 * Until script 275 nobody could see them. The only route to a verified
 * identity ran through fn_review_provider_document, which needs a provider
 * document submission — so a landlord who is not a provider had no route at
 * all, and could not add their first unit. On the corpus, 108 landlords held
 * units and every one was already verified; not one pending record had a unit.
 *
 * NO NAMES. The queue carries a Connect Code, which room they are in, what is
 * on file and how long they have waited. Opening the record itself is a
 * separate act under view_kyc_document, and it logs.
 */
export async function fetchKycQueue() {
  if (!isSupabaseConfigured()) return fail('This portal cannot reach the database.');
  const { data, error } = await supabase.rpc('fn_kyc_queue');
  if (error) {
    if (error.code === 'PGRST202') {
      return fail('The identity queue is not on this database yet — script 275 has not been run.');
    }
    if (error.code === '42501') {
      return fail('Your account does not hold See the KYC queue.');
    }
    return fail(error.message || 'The queue could not be loaded.');
  }
  return { data: data || [], error: null };
}

/**
 * Verify or reject one person's identity.
 *
 * A REASON ON EVERY DECISION, not only on a rejection — and that is the
 * difference from reviewProviderDocument above, not an inconsistency. The log
 * IS the gate here: fn_decide_an_identity calls fn_log_my_admin_action before
 * it resolves the code, so a decision with no reason is refused before
 * anything about the person is read. The database floor is eight characters,
 * and a rejection needs ten of its own on top, because a rejection the person
 * cannot act on is worse than no answer.
 *
 * Both checks are repeated here so somebody is told before they press, not by
 * Postgres afterwards — the server remains the thing that decides.
 */
export async function decideAnIdentity({ code, accept, reason }) {
  if (!isSupabaseConfigured()) return fail('This portal cannot reach the database.');
  if (!(code || '').trim()) return fail('Choose somebody from the queue.');
  if ((reason || '').trim().length < 8) {
    return fail('A few more words — the reason is recorded before the decision is.');
  }
  if (!accept && (reason || '').trim().length < 10) {
    return fail('Say why, in a sentence they can act on. "No" tells them nothing to fix.');
  }

  const { data, error } = await supabase.rpc('fn_decide_an_identity', {
    p_code: code.trim(),
    p_accept: Boolean(accept),
    p_reason: reason.trim(),
  });
  if (error) {
    if (error.code === 'PGRST202') {
      return fail('Deciding an identity is not on this database yet — script 275 has not been run.');
    }
    return fail(error.message || 'That decision could not be recorded.');
  }
  const row = Array.isArray(data) ? data[0] : data;
  return { data: row, error: null };
}

/** A signed link to the document itself, valid for a few minutes. */
export async function openProviderDocument(storagePath) {
  if (!isSupabaseConfigured()) return fail('This portal cannot reach the database.');
  if (!storagePath) return fail('There is no document on that submission.');
  const { data, error } = await supabase.storage
    .from('provider-documents')
    .createSignedUrl(storagePath, 300);
  if (error) return fail(error.message || 'That document could not be opened.');
  return { data: data?.signedUrl || null, error: null };
}

/**
 * A signed link to an ownership document, valid for a few minutes.
 *
 * THE FOUNDER FOUND THIS BY USING IT: "I saw the document in the queue, but I
 * couldn't open it to actually see what kind of document it was."
 *
 * The ownership queue has printed the raw storage path as text since it was
 * built, and the Portal could sign a URL for exactly two buckets -- provider
 * documents and the SOP shelf. Neither of them is this one. The screen's own
 * standing text told the desk to "open the document, match the erf number and
 * township against what is on it", and there was nothing to open it with.
 *
 * A SEPARATE FUNCTION FROM openProviderDocument, NOT A BUCKET ARGUMENT. Which
 * bucket a desk may reach into is a decision each of these makes on its own,
 * and a `bucket` parameter would turn two audited decisions into one call site
 * that takes the bucket from whoever is calling.
 *
 * FIVE MINUTES, the same as the provider one. Long enough to open and read a
 * title deed, short enough that a copied URL is not a lasting hole in a bucket
 * holding people's full names and residential addresses.
 *
 * SCRIPT 296 IS THE OTHER HALF. Until it, `ownership_documents_own_read`
 * admitted only the uploader and a seat holding `all` -- so this function would
 * have 403'd for the very desk the queue is gated on. It now names
 * `record_ownership_proof`, which is the permission fn_ownership_queue already
 * demands.
 */
export async function openOwnershipDocument(storagePath) {
  if (!isSupabaseConfigured()) return fail('This portal cannot reach the database.');
  if (!storagePath) return fail('There is no document on that submission.');
  const { data, error } = await supabase.storage
    .from('ownership-documents')
    .createSignedUrl(storagePath, 300);
  if (error) {
    // A refusal here is almost always the storage policy rather than a missing
    // file, and a desk reading "Object not found" would go looking for the
    // wrong thing. Script 296 is what makes this reachable at all.
    return fail(error.message || 'That document could not be opened.');
  }
  return { data: data?.signedUrl || null, error: null };
}

/** What each kind of document is, said the way a reviewer would say it. */
export const DOCUMENT_KINDS = {
  owner_identity: {
    label: 'Owner identity',
    check: 'The name and ID number must match the person who registered the business.',
  },
  cipc_registration: {
    label: 'Company registration',
    check: 'The registration number on the certificate must match what they typed.',
  },
  public_liability_insurance: {
    label: 'Public liability cover',
    check: 'Check the cover amount and the date it runs to. Expired cover is a rejection.',
  },
  trade_certificate: {
    label: 'Trade certificate',
    check: 'Check it is current and issued by the body named for that trade.',
  },
};

export const documentKind = (kind) =>
  DOCUMENT_KINDS[kind] || { label: (kind || '').replace(/_/g, ' '), check: null };

/**
 * EVERYTHING ONE ID NUMBER TOUCHES.
 *
 *   "a single ID can be tied to more than one provider profile ... I feel like
 *    there are too many doors, and too many opportunities for the same ID,
 *    unhashed, to do a lot of things at once. this must have a tight rein on it."
 *
 * The rein is in the database — scripts 99 and 101 refuse the things that were
 * actually wrong. This is the instrument that shows whether those rules are
 * holding: the person, businesses they own, businesses they are named in as a
 * beneficial owner, field and office seats, a staff seat, and anything already
 * flagged as a conflict.
 *
 * A READ, and the most sensitive one on the platform. fn_identity_footprint
 * gates it on view_kyc_document — which canon marks requires_mfa, so the asker
 * has an authenticator and has used it this session — and records the reason
 * before a single row is read. The number itself is hashed on the way in and
 * never comes back out.
 */
export async function identityFootprint({ lookup, idNumber, reason }) {
  if (!isSupabaseConfigured()) return fail('This portal cannot reach the database.');

  // `idNumber` is still accepted so an older caller keeps working; `lookup` is
  // the name that tells the truth now that either key opens this door.
  const term = String(lookup ?? idNumber ?? '').trim();
  if (!term) return fail('An ID number or an OFX Connect Code is needed to look one up.');

  const args = { p_reason: String(reason || '').trim() };

  // TWO ARGUMENT NAMES, AND NOT BECAUSE ANYBODY IS UNSURE WHICH IS RIGHT.
  //
  // Script 185 renames the first parameter from p_id_number to p_lookup, since
  // it now takes an OFX Connect Code as readily as an ID number. PostgREST
  // dispatches on ARGUMENT NAMES, so the moment this portal ships with the new
  // name it stops working against a database that has not had 185 applied --
  // and the migrations are applied by hand, on their own schedule, well after
  // a deploy.
  //
  // So it asks for the new name and falls back to the old one on the single
  // error that means "no function with those arguments". The fallback is
  // deletable the day production is known to be on 185 or later; until then it
  // is the difference between a screen that works and a screen that is waiting
  // for somebody to run a script.
  let { data, error } = await supabase.rpc('fn_identity_footprint', {
    ...args, p_lookup: term,
  });

  if (error && error.code === 'PGRST202') {
    ({ data, error } = await supabase.rpc('fn_identity_footprint', {
      ...args, p_id_number: term,
    }));
  }

  if (error) {
    if (error.code === 'PGRST202') {
      return fail('This portal needs a database update that has not been applied yet.');
    }
    return fail(error.message || 'That identity could not be looked up.');
  }
  return { data: data || [], error: null };
}

/** What each kind of attachment is called on screen. */
export const FOOTPRINT_KINDS = {
  person: 'The person',
  business_owned: 'Owns this business',
  beneficial_owner: 'Named as a beneficial owner',
  field_seat: 'Field seat',
  office_seat: 'Office seat',
  staff_seat: 'Seat at Orchid',
  conflict_flag: 'Already flagged',
};

/**
 * MOVE AN IDENTITY TO THE PROFILE IT BELONGS TO — script 268.
 *
 * The founder found his own ID attached to a Callout technician test account
 * rather than to the provider profile his email signs into, and nothing in the
 * platform could move it. `anonymize_profile` looked like the answer and has
 * never worked — it sets `profiles.deleted_at`, a column that does not exist,
 * and raises 42703 on every call.
 *
 * EITHER KEY, because a desk holds Connect Codes and not UUIDs. The footprint
 * returns no profile id at all, so a uuid-only writer could not have been
 * called from this screen; codes alone would fail on a profile that never
 * completed member signup, which is exactly the account an identity lands on by
 * mistake. `fn_profile_for_key` decides which is which.
 *
 * THE REASON IS THIS ACT'S OWN. Reading somebody's footprint and moving their
 * identity are two disclosures, not one, and each is logged under the
 * permission that governs it — view_kyc_document for the read,
 * reattach_an_identity for the move.
 */
export async function reattachIdentity({ from, to, reason }) {
  if (!isSupabaseConfigured()) return fail('This portal cannot reach the database.');

  const a = String(from || '').trim();
  const b = String(to || '').trim();
  if (!a || !b) return fail('Both the profile it is on and the profile it belongs to are needed.');
  if (a.toUpperCase() === b.toUpperCase()) return fail('Those are the same profile.');

  const { data, error } = await supabase.rpc('fn_reattach_an_identity', {
    p_from: a, p_to: b, p_reason: String(reason || '').trim(),
  });

  if (error) {
    if (error.code === 'PGRST202') {
      return fail('This portal needs a database update that has not been applied yet — script 268.');
    }
    // The database's own words. A screen that paraphrases a refusal is a screen
    // that will eventually paraphrase one it has not seen.
    const hint = error.hint ? ` ${error.hint}` : '';
    return fail(`${error.message || 'That identity could not be moved.'}${hint}`);
  }

  const row = Array.isArray(data) ? data[0] : data;
  return { data: row || null, error: null };
}

// ---------------------------------------------------------------------------
// OWNERSHIP — THE UNITS WAITING ON A COMPLIANCE SEAT TO READ A DEED
//
// The founder's rule: "Every unit must be proven owned before it can operate.
// Proof is recorded by a compliance seat, never claimed by the landlord."
//
// Both halves of that were enforced in the database and NEITHER was reachable
// from here. A landlord who signed up, was verified and added their first unit
// then could not invite a tenant to it -- correctly -- and their title deed
// went into a pending row that no screen in either repository listed. Script
// 245 named the permission and said so in its own header: "After this script
// the seat EXISTS and is empty." This is what fills it.
//
// NO NAMES. The queue carries the stand -- erf, township, address -- because a
// compliance seat's whole job here is to match those against the document. It
// does not carry the landlord's name, and deliberately not properties.owner_name,
// which for a managing-agent mandate is a natural person's.
// ---------------------------------------------------------------------------

/** Units with a document waiting to be read. Oldest first. */
export async function fetchOwnershipQueue() {
  if (!isSupabaseConfigured()) return fail('This portal cannot reach the database.');
  const { data, error } = await supabase.rpc('fn_ownership_queue');
  if (error) {
    if (error.code === 'PGRST202') {
      return fail('The ownership queue is not on this database yet — script 276 has not been run.');
    }
    if (error.code === '42501') {
      return fail('Your account does not hold Record ownership proof.');
    }
    return fail(error.message || 'The queue could not be loaded.');
  }
  return { data: data || [], error: null };
}

/**
 * Record that a unit's ownership is proven.
 *
 * A REASON ON EVERY USE, and this is new in script 276 rather than a habit
 * copied from the screens above. The permission has always carried
 * requires_mfa and requires_reason_capture; the function enforced neither,
 * because it logged under an action name that is not a permission key and the
 * reason trigger keys on the permission. It now goes through
 * fn_log_my_admin_action like every other consequential staff act.
 */
export async function recordOwnershipProof({
  propertyId, proofType, documentPath, documentDate, ownerName, reason,
}) {
  if (!isSupabaseConfigured()) return fail('This portal cannot reach the database.');
  if (!propertyId) return fail('Choose a unit from the queue.');
  if ((reason || '').trim().length < 8) {
    return fail('A few more words — the reason is recorded before the decision is.');
  }
  if (proofType === 'managing_agent_mandate' && !(ownerName || '').trim()) {
    return fail('A mandate has to say whose stand it is.');
  }

  const { data, error } = await supabase.rpc('fn_record_ownership_proof', {
    p_property_id: propertyId,
    p_proof_type: proofType,
    p_document_path: documentPath,
    p_document_date: documentDate || null,
    p_owner_name: (ownerName || '').trim() || null,
    p_reason: reason.trim(),
  });
  if (error) {
    if (error.code === 'PGRST202') {
      return fail('Recording a proof needs script 276 — this database is behind it.');
    }
    return fail(error.message || 'That proof could not be recorded.');
  }
  const row = Array.isArray(data) ? data[0] : data;
  return { data: row, error: null };
}

/**
 * Refuse an ownership document, with a reason the landlord reads.
 *
 * TEN CHARACTERS, not eight. The log's floor is about the company's record;
 * this sentence is returned to the landlord on their own portfolio screen, and
 * "wrong doc" tells them nothing to fix.
 *
 * REFUSING A DOCUMENT IS NOT RETRACTING A PROOF. A unit that is already proved
 * and whose landlord has sent a further document keeps operating when that
 * document is refused — the queue marks those rows, because the consequence of
 * a refusal is different for them and a desk should know before it decides.
 */
export async function refuseOwnershipProof({ propertyId, reason }) {
  if (!isSupabaseConfigured()) return fail('This portal cannot reach the database.');
  if (!propertyId) return fail('Choose a unit from the queue.');
  if ((reason || '').trim().length < 10) {
    return fail('Say why, in a sentence the landlord can act on. They read this.');
  }

  const { data, error } = await supabase.rpc('fn_refuse_an_ownership_proof', {
    p_property_id: propertyId,
    p_reason: reason.trim(),
  });
  if (error) {
    if (error.code === 'PGRST202') {
      return fail('Refusing a proof is not on this database yet — script 276 has not been run.');
    }
    return fail(error.message || 'That refusal could not be recorded.');
  }
  const row = Array.isArray(data) ? data[0] : data;
  return { data: row, error: null };
}

/**
 * THE TRUST TASK VERIFICATION QUEUE — the terminal step of the platform's centre.
 *
 * WHAT WAS MISSING, AND IT WAS THE LAST STEP OF THE STATE MACHINE.
 *
 * The Trust Task engine has eight writers and the consumer app reaches most of
 * them: a task is created, assigned, accepted, started, advanced, evidenced,
 * sealed and submitted. `fn_verify_a_trust_task` moves it out of `submitted`,
 * and until now NOTHING called it — not a screen, not a lib, not a test, in any
 * of the three repositories. A submitted task stopped there permanently.
 *
 * IT BELONGS HERE AND NOT IN THE PLATFORM APP. fn_verify_a_trust_task opens
 * with fn_log_my_admin_action('approve_verification', ...), which refuses a
 * caller who is not active staff. It is a compliance-seat act.
 *
 * And the queue could not be read from the platform either: the only reader of
 * trust_tasks scopes to auth.uid(), so a desk would have seen nothing — and
 * correctly, because Canon 1.2 keeps staff identity separate from platform
 * identity and a desk is not "the landlord of" anything. Script 290 built the
 * reader these two calls use.
 *
 * THE SPLIT IS THE SAME ONE THIS FILE OPENS WITH. The queue carries no name and
 * no personal code — it is a work list, so refreshing it is not an act of
 * personal-data access and needs no reason. The VERIFY carries a reason,
 * because the database demands one before it resolves anything: Canon 4.2, and
 * the log entry is the gate rather than a note kept beside one.
 */
export async function fetchTrustTaskQueue() {
  if (!isSupabaseConfigured()) return fail('This portal cannot reach the database.');
  const { data, error } = await supabase.rpc('fn_trust_tasks_awaiting_verification');
  if (error) {
    if (error.code === 'PGRST202') {
      return fail('The trust task queue is not on this database yet — script 290 has not been run.');
    }
    if (error.code === '42501') {
      return fail('Your account does not hold Approve Verification.');
    }
    return fail(error.message || 'The queue could not be loaded.');
  }
  return { data: data || [], error: null };
}

/**
 * VERIFY ONE TASK.
 *
 * The ten-character floor is this module's, on top of the database's own: a
 * verification is a permanent record on somebody's standing, and "ok" is not a
 * reason anybody can audit later. Same shape as refuseAnOwnershipProof above.
 *
 * NO CLIENT-SIDE CHECK OF WHETHER THE TASK CAN BE VERIFIED. The queue's
 * can_be_verified column says so and the screen uses it to choose what to
 * offer — but the DATABASE is what refuses a task with no integrity seal, and
 * re-deriving that rule here would be a second implementation of it that drifts
 * the first time either moves.
 */
export async function verifyTrustTask({ taskId, reason }) {
  if (!isSupabaseConfigured()) return fail('This portal cannot reach the database.');
  if (!taskId) return fail('Choose a task from the queue.');
  if ((reason || '').trim().length < 10) {
    return fail('Say what you checked, in a sentence. This is the permanent record of why.');
  }

  const { data, error } = await supabase.rpc('fn_verify_a_trust_task', {
    p_task_id: taskId,
    p_reason: reason.trim(),
  });
  if (error) {
    if (error.code === 'PGRST202') {
      return fail('Verifying a trust task is not on this database yet.');
    }
    if (error.code === '42501') {
      return fail('Your account does not hold Approve Verification.');
    }
    return fail(error.message || 'That verification could not be recorded.');
  }
  const row = Array.isArray(data) ? data[0] : data;
  return { data: row, error: null };
}

// ---------------------------------------------------------------------------
// LANDLORD BUSINESSES WAITING TO BE LISTED
//
// The founder, 16 September 2026: "as a landlord, I asked to be listed, but
// nowhere in the portal do I see anything that needs to be approved so that the
// landlord's 'business' can show up in searches."
//
// THE SAME SHAPE AS THE OWNERSHIP QUEUE ABOVE, and the second time this exact
// thing has been found. `landlord_portfolios` carries both halves of Immutable
// Rule 4 -- `is_listed` is the landlord asking, `approved_at` is Orchid
// verifying, and `visible` is both. The landlord's half was fully built and
// reachable. The verifying half was `fn_admin_review_portfolio`, which NOTHING
// in any repository called.
//
// AND IT COULD NEVER HAVE WORKED ANYWAY. Driving it on a real row raised 23503:
// `approved_by` referenced `profiles(id)`, the CONSUMER identity table, while
// the approver is staff. Canon 1.2 written into a foreign key. Platform script
// 343 repairs that, names the permission `verify_landlord_business` rather than
// the `'all'` super-permission, and adds the queue this reads.
//
// NO NAMES. The queue carries the BUSINESS -- its name, its cities, its units
// and how many are proven owned -- because verifying a business against its
// units is the job. It does not carry the landlord's personal name;
// `ofx_connect_code` identifies the account without naming anybody.
// Prohibition 6.
// ---------------------------------------------------------------------------

/** Landlord businesses that have asked to be listed. Oldest ask first. */
export async function fetchLandlordBusinessQueue() {
  if (!isSupabaseConfigured()) return fail('This portal cannot reach the database.');
  const { data, error } = await supabase.rpc('fn_landlord_business_queue');
  if (error) {
    if (error.code === 'PGRST202') {
      return fail('The landlord business queue is not on this database yet — script 343 has not been run.');
    }
    if (error.code === '42501') {
      return fail('Your account does not hold Verify a landlord business.');
    }
    return fail(error.message || 'The queue could not be loaded.');
  }
  return { data: data || [], error: null };
}

/**
 * Decide a landlord business: verify it, decline it, or withdraw a verification.
 *
 * THE ASYMMETRY THIS FILE USED TO DESCRIBE IS GONE, and the note is kept
 * because it was right at the time and the reasoning still holds.
 *
 * It said: *"the reason is required here and not by the database ... asking for
 * one here and discarding it would be worse than not asking ... If a reason
 * should be on the record, that is a platform migration adding a column and an
 * argument, not a text box on a screen that throws it away."*
 *
 * Platform script 351 is that migration. `verify_landlord_business` has carried
 * `requires_reason_capture = true` since script 343 -- which is what renders the
 * red "Reason required" pill on this card -- over a function that could not
 * store one. The screen had to apologise for it in its own copy. Canon 4.2 is
 * "the reason is recorded before the record renders", and a permission that
 * DECLARES a reason over a function that cannot keep one is that rule stated and
 * then not kept.
 *
 * THREE DECISIONS, NOT A BOOLEAN. A desk that can only say yes is a formality
 * with a button: before 351 the only acts were verify and un-verify, so a
 * business that must NOT be listed could only be LEFT in the queue for ever,
 * with no record that anybody had looked at it.
 *
 * AND `verified` CAN BE REFUSED. A business with no unit proven owned has
 * nothing to be verified from -- the permission's own description says "from the
 * units it has had proven owned" -- so the database refuses it rather than
 * leaving the check in the reviewer's head. Immutable Rule 4: eligibility is
 * derived from verified compliance, never declared.
 */
export const BUSINESS_DECISIONS = ['verified', 'declined', 'withdrawn'];

export async function decideLandlordBusiness(portfolioId, decision, reason) {
  if (!isSupabaseConfigured()) return fail('This portal cannot reach the database.');
  if (!portfolioId) return fail('Choose a business from the queue.');
  if (!BUSINESS_DECISIONS.includes(decision)) {
    return fail('Say whether this is being verified, declined or withdrawn.');
  }
  // REFUSED HERE TOO, AND NOT ONLY AT THE DATABASE. A round trip that comes
  // back with "it needs a reason" is a slower way of saying what the screen
  // already knows; the database keeps its own check because this one is a
  // convenience and that one is the rule.
  const said = String(reason || '').trim();
  if (!said) return fail('This decision is recorded with your name against it, so it needs a reason.');

  const { error } = await supabase.rpc('fn_admin_decide_landlord_business', {
    p_portfolio_id: portfolioId,
    p_decision: decision,
    p_reason: said,
  });
  if (error) {
    if (error.code === 'PGRST202') {
      return fail('Deciding a landlord business is not on this database yet — script 351 has not been run.');
    }
    if (error.code === '42501') {
      return fail('Your account does not hold Verify a landlord business.');
    }
    // 23514 is the database refusing to list a business with nothing proven
    // owned. Its own sentence says what is missing and what puts it right, so
    // it is passed through rather than replaced with something vaguer.
    return fail(error.message || 'That decision could not be recorded.');
  }
  return { data: true, error: null };
}

// ---------------------------------------------------------------------------
// A REPORTED PHOTOGRAPH OF A UNIT (platform script 344)
//
// The founder ruled photographs go STRAIGHT THROUGH and are REPORTABLE: a
// listing photograph appears the moment it is uploaded, on a unit that is
// proven owned and on a listed, approved business, and anybody signed in can
// report it. A report hides it at once, PENDING REVIEW.
//
// "Pending review" is what this desk is. Without it, a report is a permanent
// deletion any signed-in person can perform on any landlord's photograph, and
// "pending" means nothing.
//
// IT NAMES NOBODY -- not the landlord and not the reporter. The desk is
// deciding about a PHOTOGRAPH, not about who complained, and a reviewer who can
// see who reported it is a reviewer who can be lobbied. Prohibition 6, and the
// same choice the ownership and landlord-business queues make.
//
// AND IT CAN TAKE DOWN A PHOTOGRAPH NOBODY REPORTED. A cleared photograph
// cannot be re-reported -- otherwise one determined person hides a competitor's
// whole portfolio on a loop, one click per round against one review per round --
// so the desk needs a way in that does not depend on somebody complaining again.
// ---------------------------------------------------------------------------

/** Reported photographs a compliance seat has not yet judged. Oldest first. */
export async function fetchReportedPhotoQueue() {
  if (!isSupabaseConfigured()) return fail('This portal cannot reach the database.');
  const { data, error } = await supabase.rpc('fn_reported_photo_queue');
  if (error) {
    if (error.code === 'PGRST202') {
      return fail('Reported photographs are not on this database yet — script 344 has not been run.');
    }
    if (error.code === '42501') {
      return fail('Your account does not hold Review a reported photograph.');
    }
    return fail(error.message || 'The queue could not be loaded.');
  }
  return { data: data || [], error: null };
}

/**
 * Judge a reported photograph: uphold the report and take it down, or clear it
 * back into the listing.
 *
 * THE REASON IS REQUIRED, AND UNLIKE THE LANDLORD BUSINESS QUEUE IT IS KEPT.
 * `review_reported_photo` declares requires_reason_capture, and the platform's
 * own log trigger refuses the action without a real one -- so a text box here
 * is not a box whose contents are discarded. It reaches `review_reason` on the
 * photograph and the admin activity log, which is why this screen has one and
 * that one does not.
 */
export async function reviewReportedPhoto(photoId, uphold, reason) {
  if (!isSupabaseConfigured()) return fail('This portal cannot reach the database.');
  if (!photoId) return fail('Choose a photograph from the queue.');
  if (typeof uphold !== 'boolean') return fail('Say whether the report is upheld or cleared.');
  // EIGHT IS THE PLATFORM'S OWN FLOOR, not a number invented here:
  // fn_admin_action_needs_a_reason refuses anything shorter. Checking it before
  // the round trip means somebody is told by the form rather than by a trigger.
  if ((reason || '').trim().length < 8) {
    return fail('Say why, in a sentence. It goes on the record with your name.');
  }

  const { error } = await supabase.rpc('fn_review_reported_unit_photo', {
    p_photo_id: photoId,
    p_uphold: uphold,
    p_reason: reason.trim(),
  });
  if (error) {
    if (error.code === 'PGRST202') {
      return fail('Reviewing a reported photograph is not on this database yet — script 344 has not been run.');
    }
    if (error.code === '42501') {
      return fail('Your account does not hold Review a reported photograph.');
    }
    return fail(error.message || 'That decision could not be recorded.');
  }
  return { data: true, error: null };
}

/**
 * A signed link to a reported unit photograph, valid for a few minutes.
 *
 * SCRIPT 344 SHIPPED WITHOUT THIS AND SAID SO. Its PR and the ledger both
 * recorded the gap in the same words: the review desk worked from the report
 * and the caption, *"which is enough to clear an obviously vexatious report and
 * not enough to uphold a borderline one"*. A desk asked to decide whether a
 * photograph shows an identifiable person, while unable to see the photograph,
 * is not a desk — it is a form.
 *
 * WHAT MADE IT POSSIBLE IS A PLATFORM POLICY, NOT THIS FUNCTION. Script 347
 * adds a third read policy on the bucket admitting a holder of
 * `review_reported_photo` to the photographs **currently awaiting judgement**,
 * and nothing else: not the ones already passed, not the ones nobody has
 * complained about, not a landlord's private gallery. The storage API enforces
 * that on every call, so a seat without the permission gets nothing here
 * however this function is written.
 *
 * FIVE MINUTES, like every other signed link in this file. These are pictures
 * of the insides of people's homes; a link that outlives the review is a copy
 * of one sitting in somebody's history.
 */
export async function openReportedPhoto(storagePath) {
  if (!isSupabaseConfigured()) return fail('This portal cannot reach the database.');
  if (!storagePath) return fail('There is no file on that report.');
  const { data, error } = await supabase.storage
    .from('unit-photos')
    .createSignedUrl(storagePath, 300);
  if (error) {
    // THE ONE REFUSAL WITH A HUMAN ANSWER. Row security is what returns nothing
    // here, and "Object not found" is what it looks like from the outside — so
    // a seat whose permission was withdrawn, or who is looking at a report
    // somebody else has already settled, is told which of those it is rather
    // than being shown a storage error.
    return fail('That photograph is no longer open for review, or your account '
      + 'does not hold Review a reported photograph.');
  }
  return { data: data?.signedUrl || null, error: null };
}
